import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { EmployerContactSubmissionDetailsChange } from './employer-contact-submission-details-change';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';
import { SubmissionDetailsFieldChangeTypeEnum } from '../employer-contact-submission-details-field-change/submission-details-field-change-type.enum';
import { EmployerUpdateContactSubmissionUtils } from '../../../submissions/utils/employer-update-contact-submission-utils';

@Component({
  selector: 'app-employer-update-contact-submission-contact-details-changes',
  templateUrl: './employer-update-contact-submission-contact-details-changes.component.html',
  styleUrls: ['./employer-update-contact-submission-contact-details-changes.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class EmployerUpdateContactSubmissionContactDetailsChangesComponent {
  @Input() set contactDetailsChanges(changes: EmployerContactSubmissionDetailsChange[] | null) {
    this._contactDetailsChanges.next(changes ?? []);
  }

  private _contactDetailsChanges = new BehaviorSubject<EmployerContactSubmissionDetailsChange[]>([]);

  private _selectedContactIndex = new BehaviorSubject<number>(0);
  public selectedContactIndex$ = this._selectedContactIndex as Observable<number>;

  public setSelectedContactIndex(index: number): void {
    this._selectedContactIndex.next(index);
  }

  public contactDetailsChangesLength$ = this._contactDetailsChanges.pipe(
    map(changes => changes.length),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  public selectedContactDetailsChanges$ = combineLatest([this.selectedContactIndex$, this._contactDetailsChanges]).pipe(
    map(([selectedIndex, contactDetailsChanges]) => {
      if (selectedIndex < 0 || selectedIndex >= contactDetailsChanges.length) {
        return null;
      }
      return contactDetailsChanges[selectedIndex];
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  public previousChangeHasSigningAuthority$ = this.selectedContactDetailsChanges$.pipe(
    map(changes => {
      if (!changes?.previous) {
        return false;
      }

      const primaryChange = changes.previous.find(change => change.key === 'hasSigningAuthority');
      return primaryChange?.value;
    })
  );

  public newChangeHasSigningAuthority$ = this.selectedContactDetailsChanges$.pipe(
    map(changes => {
      if (!changes?.new) {
        return false;
      }

      const primaryChange = changes.new.find(change => change.key === 'hasSigningAuthority');
      return primaryChange?.value;
    })
  );

  public formattedPreviousContactChanges$ = this.selectedContactDetailsChanges$.pipe(
    map(changes =>
      changes?.previous
        ?.filter(change => change.key !== 'hasSigningAuthority')
        ?.map(change => EmployerUpdateContactSubmissionUtils.formatContactDetailsChanges(change))
    )
  );

  public formattedNewContactChanges$ = this.selectedContactDetailsChanges$.pipe(
    map(changes =>
      changes?.new
        ?.filter(change => change.key !== 'hasSigningAuthority')
        ?.map(change => EmployerUpdateContactSubmissionUtils.formatContactDetailsChanges(change))
    )
  );

  public previousNotApplicable$ = this.selectedContactDetailsChanges$.pipe(
    map(changes => {
      if (changes) {
        return changes.new.length && !changes.previous.length;
      } else {
        return false;
      }
    })
  );

  protected readonly SubmissionDetailsFieldChangeTypeEnum = SubmissionDetailsFieldChangeTypeEnum;
}
